import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import useSWR from "swr";
import { cn } from "~/lib/utils";
import { type ArtifactPreview } from "~/services/artifacts";
import { apiClient } from "~/utils/apiClient";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";
import { hasFileSuffix } from "./inlineViewerPreview";
import {
  parseProteinFeatures,
  toNightingaleData,
  type NightingaleData,
  type ProteinFeatures,
} from "./proteinFeatures";
import { useInlineViewerUrl } from "./useInlineViewerUrl";

const PROTEIN_FEATURES_SUFFIXES = [".protein-features.json"] as const;
const MAX_PROTEIN_FEATURES_BYTES = 5 * 1024 * 1024;

export function isProteinFeaturesArtifact(preview: ArtifactPreview): boolean {
  return hasFileSuffix(preview, PROTEIN_FEATURES_SUFFIXES);
}

interface LoadedFeatures {
  doc: ProteinFeatures;
  data: NightingaleData;
}

async function loadProteinFeatures(url: string): Promise<LoadedFeatures> {
  const response = await apiClient<Response>(url, { expectsResponse: true });
  const contentLength = response.headers.get("Content-Length");
  if (contentLength && Number(contentLength) > MAX_PROTEIN_FEATURES_BYTES) {
    throw new Error("File is too large to render inline. Please download it instead.");
  }
  const doc = parseProteinFeatures(await response.json());
  // Importing the packages registers their custom elements.
  await Promise.all([
    import("@nightingale-elements/nightingale-manager"),
    import("@nightingale-elements/nightingale-navigation"),
    import("@nightingale-elements/nightingale-sequence"),
    import("@nightingale-elements/nightingale-track"),
    import("@nightingale-elements/nightingale-variation"),
  ]);
  return { doc, data: toNightingaleData(doc) };
}

interface ProteinFeaturesViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

const ProteinFeaturesViewer = ({ preview, className }: ProteinFeaturesViewerProps) => {
  const url = useInlineViewerUrl(preview);
  // Keyed on the held URL, so the inline card and the expanded view share one
  // request, and an unchanged document is not fetched again on focus.
  const { data: loaded, error: loadError } = useSWR(
    url ? ["protein-features", url] : null,
    ([, documentUrl]) => loadProteinFeatures(documentUrl),
    { revalidateOnFocus: false }
  );
  const error = url
    ? loadError && (loadError instanceof Error ? loadError.message : "Unknown error")
    : "No content URL available for this artifact.";

  if (error) {
    return (
      <div className="flex items-center justify-center h-48 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="text-center">
          <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading protein view...</p>
        </div>
      </div>
    );
  }

  const { doc, data } = loaded;
  const range = { length: data.length, "display-start": 1, "display-end": data.length };

  return (
    <div className={cn("w-full overflow-auto rounded-md bg-background p-2", className)}>
      <p className="mb-2 text-xs text-muted-foreground">
        {[doc.protein.accession, doc.protein.name].filter(Boolean).join(" · ")}
      </p>
      <nightingale-manager {...range}>
        <div className="grid grid-cols-[8rem_1fr] items-center gap-y-1 text-xs">
          <span />
          <nightingale-navigation {...range} height={40} />
          <span className="text-muted-foreground">Sequence</span>
          <nightingale-sequence
            {...range}
            height={30}
            highlight-event="onmouseover"
            ref={assignData(data.sequence)}
          />
          {data.tracks.map((track) => (
            <TrackRow key={track.label} label={track.label}>
              <nightingale-track
                {...range}
                height={20}
                layout="non-overlapping"
                highlight-event="onmouseover"
                ref={assignData(track.features)}
              />
            </TrackRow>
          ))}
          <TrackRow label="Variant positions">
            <nightingale-track
              {...range}
              height={20}
              highlight-event="onmouseover"
              ref={assignData(data.variantPositions)}
            />
          </TrackRow>
          <TrackRow label="Residue changes">
            <nightingale-variation
              {...range}
              height={240}
              condensed-view
              highlight-event="onmouseover"
              ref={assignData(data.variation)}
            />
          </TrackRow>
        </div>
      </nightingale-manager>
    </div>
  );
};

const TrackRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <span className="truncate pr-2 text-muted-foreground" title={label}>
      {label}
    </span>
    {children}
  </>
);

// Nightingale elements take their data as a JS property; React would stringify
// an object passed as an attribute to a custom element it can't introspect.
function assignData(data: unknown) {
  return (el: HTMLElement | null) => {
    if (el) Object.assign(el, { data });
  };
}

export const ProteinFeaturesArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<ProteinFeaturesViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <ProteinFeaturesViewer preview={preview} />
    </div>
  </ArtifactShell>
);
